import {
  IComponentDefinition,
  IDisplayItem,
  IDisplayItemComponent,
  IScreenDesignDictionary
} from "@kuika/kuika-cl-model";

/**
 * Move up / Move down for one display item among its siblings — shared by the component tree's row
 * buttons and the canvas selection overlay's ⋯ menu, so both answer "can this move?" identically.
 *
 * A child the parent's definition declares `fixed` (a Tab's header, a Form's footer…) never moves and
 * never gets swapped with, so both the item itself and the neighbour it would trade places with are
 * checked.
 */
export type ComponentDefinitionResolver = (item: IDisplayItemComponent) => IComponentDefinition | undefined;

/** Arguments for `reOrderItems(source, target, nextSelected)`: `source` is placed before `target`. */
export interface ISiblingReorder {
  sourceKuikaId: string;
  targetKuikaId: string;
  nextSelectedKuikaId: string;
}

const getSiblings = (dict: IScreenDesignDictionary, kuikaId: number): IDisplayItem[] | undefined => {
  const parentId: number | undefined = dict[kuikaId]?.parentId;
  if (!parentId) {
    return undefined;
  }
  return dict[parentId]?.children;
};

export const isFixedChild = (
  dict: IScreenDesignDictionary,
  di: IDisplayItem | undefined,
  resolveDefinition: ComponentDefinitionResolver
): boolean => {
  const parentId: number | undefined = di?.parentId;
  if (!di || !parentId) {
    return false;
  }
  const parent: IDisplayItem | undefined = dict[parentId];
  if (!parent) {
    return false;
  }
  const parentDefinition: IComponentDefinition | undefined = resolveDefinition(parent.item);
  return parentDefinition?.childrenInfo?.find((child) => child.name === di.item.name)?.fixed === true;
};

// Compared as numbers: a child's `id` can arrive as a string (collab / persisted payloads), which a strict
// `===` against the numeric kuikaId would never match — the item would read as "not among its siblings".
const indexAmongSiblings = (siblings: IDisplayItem[], kuikaId: number): number =>
  siblings.findIndex((sibling: IDisplayItem): boolean => Number(sibling.id) === kuikaId);

export const canMoveUp = (
  dict: IScreenDesignDictionary,
  kuikaId: number,
  resolveDefinition: ComponentDefinitionResolver
): boolean => {
  const siblings: IDisplayItem[] | undefined = getSiblings(dict, kuikaId);
  if (!siblings || isFixedChild(dict, dict[kuikaId], resolveDefinition)) {
    return false;
  }
  const idx: number = indexAmongSiblings(siblings, kuikaId);
  return idx > 0 && !isFixedChild(dict, siblings[idx - 1], resolveDefinition);
};

export const canMoveDown = (
  dict: IScreenDesignDictionary,
  kuikaId: number,
  resolveDefinition: ComponentDefinitionResolver
): boolean => {
  const siblings: IDisplayItem[] | undefined = getSiblings(dict, kuikaId);
  if (!siblings || isFixedChild(dict, dict[kuikaId], resolveDefinition)) {
    return false;
  }
  const idx: number = indexAmongSiblings(siblings, kuikaId);
  return idx !== -1 && idx < siblings.length - 1 && !isFixedChild(dict, siblings[idx + 1], resolveDefinition);
};

/** The reorder that moves `kuikaId` one slot up (before its previous sibling), or null at the top. */
export const getMoveUpReorder = (dict: IScreenDesignDictionary, kuikaId: number): ISiblingReorder | null => {
  const siblings: IDisplayItem[] | undefined = getSiblings(dict, kuikaId);
  if (!siblings) {
    return null;
  }
  const idx: number = indexAmongSiblings(siblings, kuikaId);
  if (idx <= 0) {
    return null;
  }
  const id: string = kuikaId.toString();
  return { sourceKuikaId: id, targetKuikaId: siblings[idx - 1].id.toString(), nextSelectedKuikaId: id };
};

/** The reorder that moves `kuikaId` one slot down (its next sibling goes before it), or null at the bottom. */
export const getMoveDownReorder = (dict: IScreenDesignDictionary, kuikaId: number): ISiblingReorder | null => {
  const siblings: IDisplayItem[] | undefined = getSiblings(dict, kuikaId);
  if (!siblings) {
    return null;
  }
  const idx: number = indexAmongSiblings(siblings, kuikaId);
  if (idx === -1 || idx >= siblings.length - 1) {
    return null;
  }
  const id: string = kuikaId.toString();
  return { sourceKuikaId: siblings[idx + 1].id.toString(), targetKuikaId: id, nextSelectedKuikaId: id };
};
